import { Inject } from '@odinjs/odin'
import { ButtonAPI, DeckAPI, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, OralDietViewerAliases } from '../../../../codegen';
import { Endpoint } from '../../../../constants/endpoints';
import BaseController from '../../../BaseController';
import { MedicationAndDateHeaderParams } from '../../../commons/header/medication-and-date-header-params';
import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E9129Parameters, FunctionName } from '../../../../utils/parameters';

interface OralDiet {
  barCode: string;
  dietCode: number;
  dietDescription: string;
  dietName: string;
  informationDescription: string;
  intervalCode: string;
  itemType: string;
  mealCode: string;
  prescriptionNumber: number;
  scheduleDate: string;
  scheduleSequenceNumber: number;
  sequenceDietNumber: number;
  statusSchedule: string;
}

@Controller({ component: OralDietViewerAliases.ORAL_DIET_VIEWER_LAYOUT, fragment: FragmentNames.ORAL_DIET_VIEWER })
class OralDietViewerController extends BaseController {

  @Inject(OralDietViewerAliases.ORAL_DIET_VIEWER_DECK)
  deckApi!: DeckAPI

  @Inject(OralDietViewerAliases.EXECUTE_TIME_PANEL)
  executeTimePanelApi!: PanelAPI;

  @OnMount(OralDietViewerAliases.ORAL_DIET_VIEWER_LAYOUT)
  onMountLayout(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    this.context.set({
      encounterId,
      patientId
    });
  }

  @OnMount(OralDietViewerAliases.ORAL_DIET_VIEWER_DECK)
  onMountDeck(event: MountEvent): void {
    this.getOralDietInformation(event.target as DeckAPI);
  }

  getOralDietInformation(deckApi: DeckAPI): void {
    const { scheduleSequenceNumber } = this.historyService.getCurrentRouteParams();

    this.deckApi.setLoading(true);
    HttpService.get<OralDiet>(Endpoint.EMAR_GET_ORAL_DIET_INFORMATION, { scheduleSequenceNumber })
      .then(data => this.onFetchOralDietInformation(data))
      .then(data => this.showOralDietInformation(data))
      .finally(() => this.stopLoadings(deckApi))
  }

  showOralDietInformation(oralDietInformation: OralDiet): void {
    MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
      medicationName: oralDietInformation.dietDescription,
      scheduleDate: oralDietInformation.scheduleDate,
      type: 'ORAL_DIET',
    }

    const deckData: Record<string, Value> = {
      ...oralDietInformation,
      dateTimeValue: oralDietInformation.scheduleDate,
      description: oralDietInformation.dietDescription,
      information: oralDietInformation.informationDescription,
      prescriptionPosition: 'LEFT',
      statusItem: oralDietInformation.statusSchedule,
    }

    this.deckApi.setData([deckData]);
    this.context.set(deckData);

    const statusSchedule = this.context.get('statusSchedule')
    if (statusSchedule !== 'A') {
      this.executeTimePanelApi.setVisible(true);
    }
  }

  onFetchOralDietInformation(fetchInformation: HttpResponse<OralDiet>): Promise<OralDiet> {
    if (!fetchInformation.body) return Promise.reject();

    const responseBody = fetchInformation.body;
    this.context.set({ responseBody })
    return Promise.resolve(responseBody);
  }

  stopLoadings(deck: DeckAPI): void {
    deck && deck.setLoading(false);
  }

  @OnMount(OralDietViewerAliases.EXECUTE_TIME_BUTTON)
  onMountExecuteTimeButton(event: MountEvent): void {
    const buttonApi = event.target as ButtonAPI;
    const allowed = this.validateExecuteTimeButton();
    buttonApi.setVisible(allowed);
    this.executeTimePanelApi.setVisible(allowed);
  }

  validateExecuteTimeButton(): boolean {
    const param13 = FoundationUtils.findModuleParam(FunctionName.ADEP_9129, ADEP_E9129Parameters.ALLOWS_TO_PERFORM_PRESCRIPTION_ITEMS) as string;
    return ['S', 'D'].includes(param13);
  }

  @OnClick(OralDietViewerAliases.EXECUTE_TIME_BUTTON)
  onClickExecuteTimeButton(): void {
    ModalService.openDefault({
      contextData: this.context.getBulkData() as Record<string, Value>,
      fragment: FragmentNames.ORAL_DIET_EXECUTE_TIME_MODAL,
      id: FragmentNames.ORAL_DIET_EXECUTE_TIME_MODAL,
      title: 'text.execute-time',
    })
  }
}

export default OralDietViewerController;
